import React from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Table from 'react-bootstrap/Table';
import LoadingSpinner from '../common/LoadingSpinner';
import type { SortDirection, UserPage, UserSortField, UserSummary } from '../../services/types';
import type { UserListQuery } from '../../services/userService';
import { PAGE_SIZE_OPTIONS } from '../../services/userService';
import { formatGermanDate } from '../../utils/dateFormat';
import { formatUserDisplayName } from './userDisplay';

/** Spaltendefinition der Benutzertabelle. */
interface UserColumn {
  /** Schluessel fuer HTML-IDs (z. B. "name" -> user-list-header-name). */
  readonly key: string;
  /** Deutsche Spaltenueberschrift. */
  readonly label: string;
  /** Sortierfeld der API oder null, wenn die Spalte nicht sortierbar ist. */
  readonly sortField: UserSortField | null;
}

/**
 * Spalten der Liste. Sortierbar sind ausschliesslich Felder aus der
 * Whitelist des Backends; "Status" hat kein Gegenstueck und bleibt
 * bewusst unsortierbar (ein Klick liefe sonst in einen HTTP 400).
 */
const COLUMNS: readonly UserColumn[] = [
  { key: 'name', label: 'Name / Firma', sortField: 'lastName' },
  { key: 'email', label: 'E-Mail', sortField: 'email' },
  { key: 'city', label: 'Ort', sortField: 'city' },
  { key: 'status', label: 'Status', sortField: null },
  { key: 'created', label: 'Registriert am', sortField: 'createdAt' },
];

/** Props der UserList-Komponente. */
export interface UserListProps {
  /** Aktuelle Abfrage (Suche, Seite, Groesse, Sortierung). */
  readonly query: UserListQuery;
  /** Ungebremster Wert des Suchfeldes (vor dem Debounce). */
  readonly searchInput: string;
  /** Geladene Seite oder null (laedt noch bzw. Fehler). */
  readonly data: UserPage | null;
  /** true, waehrend die Liste geladen wird. */
  readonly loading: boolean;
  /** Deutsche Fehlermeldung oder null. */
  readonly error: string | null;
  /** ID des aktuell gewaehlten Benutzers (Zeile wird hervorgehoben). */
  readonly selectedUserId: string | null;
  /** Meldet eine Eingabe im Suchfeld (unterliegt dem Debounce). */
  readonly onSearchChange: (value: string) => void;
  /** Meldet den Klick auf "Suche zurücksetzen" (wirkt sofort). */
  readonly onSearchClear: () => void;
  /** Meldet einen Klick auf einen sortierbaren Spaltenkopf. */
  readonly onSortChange: (field: UserSortField) => void;
  /** Meldet einen Seitenwechsel (nullbasiert). */
  readonly onPageChange: (page: number) => void;
  /** Meldet eine geaenderte Seitengroesse. */
  readonly onPageSizeChange: (size: number) => void;
  /** Meldet die Auswahl eines Benutzers. */
  readonly onSelectUser: (userId: string) => void;
}

/** Bootstrap-Icon des Sortier-Indikators je Spaltenzustand. */
function sortIconClass(active: boolean, direction: SortDirection): string {
  if (!active) {
    return 'bi-arrow-down-up text-muted';
  }
  return direction === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill';
}

/** Statuszelle: Kontostatus und Abo-Status als Badges. */
function StatusCell({ user }: { readonly user: UserSummary }): React.JSX.Element {
  return (
    <div id={`user-row-${user.id}-status`} className="d-flex flex-wrap gap-1">
      <Badge
        id={`user-row-${user.id}-status-active`}
        bg={user.active ? 'success' : 'secondary'}
      >
        {user.active ? 'Aktiv' : 'Inaktiv'}
      </Badge>
      {user.subscriptionActive && (
        <Badge id={`user-row-${user.id}-status-subscription`} bg="info" text="dark">
          Abo
        </Badge>
      )}
    </div>
  );
}

/**
 * Tabellarische Benutzerliste des Master-Detail-Bereichs mit Suchfeld,
 * sortierbaren Spaltenkoepfen und Pagination.
 *
 * Die Komponente ist rein darstellend: Abfragezustand und Daten kommen
 * von der Seite (UsersPage), alle Interaktionen werden nach oben
 * gemeldet. Ein Klick auf eine Zeile waehlt den Benutzer aus; die
 * gewaehlte Zeile ist hervorgehoben (table-active).
 *
 * Die Fusszeile (Seitengroesse + Blaettern) bleibt in allen Zustaenden
 * sichtbar, damit das Layout beim Laden nicht springt; die Bedienelemente
 * sind waehrend eines Requests deaktiviert.
 */
function UserList({
  query,
  searchInput,
  data,
  loading,
  error,
  selectedUserId,
  onSearchChange,
  onSearchClear,
  onSortChange,
  onPageChange,
  onPageSizeChange,
  onSelectUser,
}: UserListProps): React.JSX.Element {
  const users: readonly UserSummary[] = data?.content ?? [];
  const totalElements = data?.totalElements ?? 0;
  const totalPages = data === null ? 0 : Math.max(data.totalPages, 1);
  // Seitenanzeige klemmen: Schrumpft die Ergebnismenge serverseitig
  // (z. B. weil Benutzer geloescht wurden), liefert das Backend zwar die
  // angefragte, jetzt leere Seite zurueck — "Seite 6 von 2" waere aber
  // unsinnig. Beim Laden (data === null) gilt die angefragte Seite.
  const requestedPage = data?.page ?? query.page;
  const currentPage = data === null ? requestedPage : Math.min(requestedPage, totalPages - 1);
  const displayedPages = Math.max(totalPages, 1);
  const hasPrevious = currentPage > 0;
  const hasNext = data !== null && currentPage + 1 < totalPages;

  return (
    <Card id="user-list-card" className="shadow-sm">
      <Card.Body id="user-list-body">
        <div
          id="user-list-toolbar"
          className="d-flex flex-wrap align-items-center gap-2 mb-3"
        >
          <InputGroup id="user-search-group" className="flex-grow-1" style={{ maxWidth: '24rem' }}>
            <InputGroup.Text id="user-search-icon-wrapper">
              <i id="user-search-icon" className="bi bi-search"></i>
            </InputGroup.Text>
            <Form.Control
              id="user-search"
              type="text"
              placeholder="Suche nach Name, E-Mail oder Ort"
              value={searchInput}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                onSearchChange(event.target.value)
              }
            />
            {searchInput !== '' && (
              <Button
                id="user-search-clear"
                variant="outline-secondary"
                title="Suche zurücksetzen"
                onClick={onSearchClear}
              >
                <i id="user-search-clear-icon" className="bi bi-x-lg"></i>
              </Button>
            )}
          </InputGroup>
        </div>

        {loading && (
          <div
            id="user-list-loading-container"
            className="d-flex justify-content-center py-5"
          >
            <LoadingSpinner id="user-list-loading" />
          </div>
        )}

        {!loading && error !== null && (
          <Alert id="user-list-error" variant="danger" className="mb-0">
            {error}
          </Alert>
        )}

        {!loading && error === null && users.length === 0 && (
          <div id="user-list-empty" className="text-center text-muted py-5">
            Keine Benutzer gefunden.
          </div>
        )}

        {!loading && error === null && users.length > 0 && (
          <div id="user-list-table-wrapper" className="table-responsive">
            <Table id="user-list" hover size="sm" className="align-middle mb-0">
              <thead id="user-list-head">
                <tr id="user-list-head-row">
                  {COLUMNS.map((column) => {
                    // Lokale Konstante statt column.sortField im Callback:
                    // Nur so bleibt die Null-Pruefung auch im onClick-Closure
                    // erhalten (kein Cast noetig).
                    const sortField: UserSortField | null = column.sortField;
                    return (
                      <th
                        key={column.key}
                        id={`user-list-header-${column.key}`}
                        scope="col"
                        className="text-nowrap"
                      >
                        {sortField === null ? (
                          column.label
                        ) : (
                          <Button
                            id={`user-sort-${sortField}`}
                            variant="link"
                            size="sm"
                            className="p-0 text-decoration-none text-reset fw-semibold"
                            onClick={() => onSortChange(sortField)}
                          >
                            {column.label}
                            <i
                              id={`user-sort-${sortField}-icon`}
                              className={`bi ms-1 ${sortIconClass(
                                query.sortField === sortField,
                                query.sortDirection
                              )}`}
                            ></i>
                          </Button>
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody id="user-list-body-rows">
                {users.map((user) => (
                  <tr
                    key={user.id}
                    id={`user-row-${user.id}`}
                    className={user.id === selectedUserId ? 'table-active' : undefined}
                    style={{ cursor: 'pointer' }}
                    onClick={() => onSelectUser(user.id)}
                  >
                    <td id={`user-row-${user.id}-name`}>
                      <div id={`user-row-${user.id}-name-primary`} className="fw-semibold">
                        {formatUserDisplayName(user)}
                      </div>
                      <div
                        id={`user-row-${user.id}-username`}
                        className="small text-muted text-truncate"
                      >
                        {user.username}
                      </div>
                    </td>
                    <td id={`user-row-${user.id}-email`} className="text-break">
                      {user.email}
                    </td>
                    <td id={`user-row-${user.id}-city`}>{user.city ?? '–'}</td>
                    <td id={`user-row-${user.id}-status-cell`}>
                      <StatusCell user={user} />
                    </td>
                    <td id={`user-row-${user.id}-created`} className="text-nowrap">
                      {formatGermanDate(user.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        <div
          id="user-list-footer"
          className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3"
        >
          <div id="user-page-size-group" className="d-flex align-items-center gap-2">
            <Form.Label id="user-page-size-label" htmlFor="user-page-size" className="mb-0 small text-muted">
              Pro Seite
            </Form.Label>
            <Form.Select
              id="user-page-size"
              size="sm"
              style={{ width: 'auto' }}
              value={query.size}
              disabled={loading}
              onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                onPageSizeChange(Number.parseInt(event.target.value, 10))
              }
            >
              {PAGE_SIZE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Form.Select>
          </div>

          <div id="user-page-controls" className="d-flex align-items-center gap-2">
            <Button
              id="user-page-prev"
              variant="outline-secondary"
              size="sm"
              disabled={loading || !hasPrevious}
              title="Vorherige Seite"
              onClick={() => onPageChange(currentPage - 1)}
            >
              <i id="user-page-prev-icon" className="bi bi-chevron-left"></i>
            </Button>
            <span id="user-page-info" className="small text-muted text-nowrap">
              {`Seite ${currentPage + 1} von ${displayedPages} (${totalElements} Benutzer)`}
            </span>
            <Button
              id="user-page-next"
              variant="outline-secondary"
              size="sm"
              disabled={loading || !hasNext}
              title="Nächste Seite"
              onClick={() => onPageChange(currentPage + 1)}
            >
              <i id="user-page-next-icon" className="bi bi-chevron-right"></i>
            </Button>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

export default UserList;
